import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';
import { Download, Info } from 'lucide-react';
import DeleteModal from '../../modals/data-source/DeleteModal';
import DetailsModal from '../../modals/data-source/DetailsModal';
import { DataSource, File, FileListProps } from '../../../types/knowledge-base';
// Import icons
import pdfIcon from '../../../assets/pdf-icon.png';
import docIcon from '../../../assets/doc-icon.png';
import docxIcon from '../../../assets/docx-icon.png';
import csvIcon from '../../../assets/csv-icon.png';
import jsonIcon from '../../../assets/json-icon.png';
import mdIcon from '../../../assets/md-icon.png';
import pptIcon from '../../../assets/ppt-icon.png';
import pptxIcon from '../../../assets/pptx-icon.png';
import txtIcon from '../../../assets/txt-icon.png';
import xlsIcon from '../../../assets/xls-icon.png';
import xlsxIcon from '../../../assets/xlsx-icon.png';

// Helper function to convert status
const convertStatus = (status: string | undefined): "active" | "pending" | "failed" => {
    switch(status) {
        case 'active':
            return 'active';
        case 'failed':
            return 'failed';
        default:
            return 'pending';
    }
};

const convertDataSourceToFile = (source: DataSource): File => ({
    id: source.id,
    name: source.metadata?.original_filename || source.name,
    fileSize: source.metadata?.file_size || 0,
    mimeType: source.metadata?.mime_type || '',
    created_at: source.created_at,
    updated_at: source.updated_at,
    description: source.metadata?.description || '',
    uploadedBy: source.created_by_name || '',
    isOwner: false,
    canManage: false,
    groupAccess: [],
    file_path: '',
    status: convertStatus(source.status),
    groups: []
});

export const FileList: React.FC<FileListProps> = ({
                                                      onDelete,
                                                      onError,
                                                      onSuccess,
                                                      refreshKey = 0
                                                  }) => {
    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedSource, setSelectedSource] = useState<DataSource | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const fetchUserRole = async () => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                console.error('No user found');
                return;
            }

            const user = JSON.parse(userStr);
            const userId = user.id;
            const roleEndpoint = API_ENDPOINTS.USERS.GET_ROLE.replace(':id', userId);
            interface RoleResponse {
                role: string;
            }
            const data = await apiService.get<RoleResponse>(roleEndpoint);
            setIsAdmin(data.role?.toLowerCase() === 'admin');
        } catch (err) {
            console.error('Error fetching user role:', err);
            onError?.(err instanceof Error ? err.message : 'Failed to fetch user role');
        }
    };

    const fetchDataSources = async () => {
        try {
            interface DataSourcesResponse {
                dataSources: DataSource[];
            }
            const data = await apiService.get<DataSourcesResponse>(API_ENDPOINTS.FILES.LIST);
            setDataSources(data.dataSources || []);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data-sources';
            setError(errorMessage);
            onError?.(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        Promise.all([fetchDataSources(), fetchUserRole()]);
    }, [refreshKey]);

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    };

    const handleDeleteClick = (source: DataSource) => {
        setSelectedSource(source);
        setShowDeleteModal(true);
    };

    const handleDetailsClick = (source: DataSource) => {
        setSelectedSource(source);
        setShowDetailsModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedSource) return;

        setIsDeleting(true);
        setDeleteError(null);

        try {
            await apiService.delete(`${API_ENDPOINTS.FILES.DELETE}/${selectedSource.id}`);
            await fetchDataSources();
            onDelete?.(selectedSource.name);
            onSuccess?.('File deleted successfully');
            setShowDeleteModal(false);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete data-source';
            setDeleteError(errorMessage);
            onError?.(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownload = async (source: DataSource) => {
        try {
            await apiService.downloadFile(
                `/api/files/download/${source.id}`,
                source.metadata?.original_filename || source.name
            );
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to download data-source';
            setError(errorMessage);
            onError?.(errorMessage);
        }
    };

    const getFileIcon = (fileName: string | undefined) => {
        if (!fileName) return '📎';
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf': return <img src={pdfIcon} alt="PDF" className="me-3 file-icon" />;
            case 'doc': return <img src={docIcon} alt="DOC" className="me-3 file-icon" />;
            case 'docx': return <img src={docxIcon} alt="DOCX" className="me-3 file-icon" />;
            case 'md': return <img src={mdIcon} alt="MD" className="me-3 file-icon" />;
            case 'xls': return <img src={xlsIcon} alt="XLS" className="me-3 file-icon" />;
            case 'xlsx': return <img src={xlsxIcon} alt="XLSX" className="me-3 file-icon" />;
            case 'csv': return <img src={csvIcon} alt="CSV" className="me-3 file-icon" />;
            case 'txt': return <img src={txtIcon} alt="TXT" className="me-3 file-icon" />;
            case 'json': return <img src={jsonIcon} alt="JSON" className="me-3 file-icon" />;
            case 'ppt': return <img src={pptIcon} alt="PPT" className="me-3 file-icon" />;
            case 'pptx': return <img src={pptxIcon} alt="PPTX" className="me-3 file-icon" />;
            default: return '📎';
        }
    };

    if (isLoading) {
        return <div className="text-center p-4">Loading...</div>;
    }

    if (error) {
        return <div className="text-center p-4 text-danger">Error: {error}</div>;
    }

    return (
        <div className="file-list">
            <table className="table table-hover mb-0">
                <thead>
                <tr>
                    <th style={{ width: '50%' }}>File</th>
                    <th className="text-center" style={{ width: '25%' }}>Actions</th>
                    <th className="text-center" style={{ width: '25%' }}>Manage</th>
                </tr>
                </thead>
                <tbody>
                {dataSources.length === 0 ? (
                    <tr><td colSpan={3} className="text-center">No files found</td></tr>
                ) : (
                    dataSources.map((source) => (
                        <tr key={source.id} className="file-item">
                            <td className="file-info">
                                <div className="d-flex align-items-center">
                                    {getFileIcon(source.metadata?.original_filename)}
                                    <div>
                                        <div className="file-name">
                                            {source.metadata?.original_filename || source.name}
                                            {source.status === 'failed' && (
                                                <span className="ms-2 badge bg-danger">Failed</span>
                                            )}
                                            {source.status === 'pending' && (
                                                <span className="ms-2 badge bg-warning">Pending</span>
                                            )}
                                        </div>
                                        <div className="text-muted small">
                                            {formatFileSize(source.metadata?.file_size || 0)} • Uploaded on{' '}
                                            {source.metadata?.upload_completed_at
                                                ? new Date(source.metadata.upload_completed_at).toLocaleDateString()
                                                : new Date(source.created_at).toLocaleDateString()
                                            } by {source.created_by_name || 'Unknown'}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="text-center">
                                <Button
                                    variant="link"
                                    className="me-2"
                                    onClick={() => handleDetailsClick(source)}
                                    title="Details"
                                    disabled={!isAdmin}
                                    style={{ opacity: isAdmin ? 1 : 0.5 }}
                                >
                                    <Info size={20} />
                                </Button>
                                <Button
                                    variant="link"
                                    onClick={() => handleDownload(source)}
                                    title="Download"
                                    disabled={!isAdmin || source.status !== 'active'}
                                    style={{ opacity: (isAdmin && source.status === 'active') ? 1 : 0.5 }}
                                >
                                    <Download size={20} />
                                </Button>
                            </td>
                            <td className="text-center">
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteClick(source)}
                                    disabled={!isAdmin}
                                    style={{ opacity: isAdmin ? 1 : 0.5 }}
                                >
                                    Delete
                                </Button>
                            </td>
                        </tr>
                    ))
                )}
                </tbody>
            </table>

            <DeleteModal
                show={showDeleteModal}
                fileName={selectedSource?.metadata?.original_filename || selectedSource?.name || null}
                isDeleting={isDeleting}
                onHide={() => {
                    setShowDeleteModal(false);
                    setDeleteError(null);
                    setSelectedSource(null);
                }}
                onConfirm={handleDeleteConfirm}
                error={deleteError}
            />

            <DetailsModal
                show={showDetailsModal}
                file={selectedSource ? convertDataSourceToFile(selectedSource) : null}
                isAdmin={isAdmin}
                formatFileSize={formatFileSize}
                onHide={() => setShowDetailsModal(false)}
            />
        </div>
    );
};

export default FileList;